/**
 * CONDIVIDERE UNA GIOCATA O UNA PRENOTAZIONE: il messaggio e dove mandarlo.
 *
 * Qui ci sono il testo che parte e gli indirizzi dei canali; il foglio con i
 * tasti lo disegna components/Condivisione/FoglioCondividi. Quali canali si
 * vedono lo decide il backoffice (le regole di regoleStampa.js); qui si
 * toglie solo quello che su questo apparecchio non puo' funzionare.
 *
 * NEL MESSAGGIO NON C'E' MAI NIENTE DI PERSONALE: non il codice della
 * giocata, non il suo identificativo, non il nome del conto, non l'agenzia.
 * Un messaggio mandato oggi a un amico domani sta in una chat di gruppo. La
 * posta e la vincita ci sono solo se chi condivide lo sceglie (o la regola lo
 * impone); sulla prenotazione l'importo c'e' sempre, perche' e' la proposta.
 *
 * I CANALI, nell'ordine in cui si mostrano:
 *   whatsapp, telegram, email, sms, x, facebook, viber: un indirizzo (wa.me,
 *     t.me, mailto:...) che apre l'applicazione se c'e' e il sito se no;
 *   signal: non ha un indirizzo per il testo. Sul telefono passa dal foglio
 *     del sistema; sul computer (puntatore fine) e senza foglio si copia il
 *     testo e lo si incolla in Signal;
 *   copia, immagine, altro: azioni, non indirizzi. «Altro…» e' il foglio del
 *     sistema (navigator.share), che conosce le applicazioni installate.
 */
import i18next from 'i18next';
import {formattaImporto, mostraQuota} from './formato';
import {valuta} from '../skin';
import {datiPrenotazione} from './stampaGiocata';

const t = (chiave, valori) => i18next.t(chiave, valori);

/** Le chiavi dei canali, nell'ordine in cui si mostrano (lo stesso del servizio). */
export const CANALI = ['whatsapp', 'telegram', 'signal', 'email', 'sms', 'x', 'facebook', 'viber',
  'copia', 'immagine', 'altro'];

/* I nomi che si leggono sui tasti. Le marche restano come sono, ma passano lo
   stesso dal dizionario: una skin puo' voler scrivere «E-mail». */
const NOMI = {
  get whatsapp() { return t('WhatsApp'); },
  get telegram() { return t('Telegram'); },
  get signal() { return t('Signal'); },
  get email() { return t('Email'); },
  get sms() { return t('SMS'); },
  get x() { return t('X'); },
  get facebook() { return t('Facebook'); },
  get viber() { return t('Viber'); },
  get copia() { return t('Copia'); },
  get immagine() { return t('Immagine'); },
  get altro() { return t('Altro…'); },
};

/** Il nome di un canale sul suo tasto. */
export function nomeCanale(chiave) {
  return NOMI[chiave] || chiave;
}

// ---------------------------------------------------------------------------
// I messaggi
// ---------------------------------------------------------------------------

const PUNTO = '• ';

function soldi(n) {
  return formattaImporto(n, {valuta: valuta()});
}

/* «La mia multipla:» quando la giocata e' di chi condivide; «Multipla:»
   quando no -- un'agenzia che condivide la giocata di un suo cliente non
   puo' dire «la mia». */
function intestazione(dati, mia) {
  const tipo = dati.tipo;
  const grandezze = (dati.grandezze || []).map((g) => {
    try { return String(g).toLocaleLowerCase(); } catch (e) { return String(g).toLowerCase(); }
  }).join(', ');
  if (tipo === 'SYSTEM') {
    if (grandezze) {
      return mia ? t('Il mio sistema ({{grandezze}}):', {grandezze}) : t('Sistema ({{grandezze}}):', {grandezze});
    }
    return mia ? t('Il mio sistema:') : t('Sistema:');
  }
  if (tipo === 'SINGLE') return mia ? t('La mia singola:') : t('Singola:');
  if (tipo === 'INTEGRAL') return mia ? t('La mia integrale:') : t('Integrale:');
  return mia ? t('La mia multipla:') : t('Multipla:');
}

function rigaSelezione(e) {
  const evento = e.evento || e.numero || '—';
  return `${PUNTO}${evento} · ${e.mercato} ${e.segno} @ ${e.quota}`;
}

function rigaQuota(dati) {
  const min = dati.quotaMin != null ? dati.quotaMin : dati.quotaMax;
  const max = dati.quotaMax != null ? dati.quotaMax : dati.quotaMin;
  if (min == null) return '';
  if (min === max) return t('Quota totale {{quota}}', {quota: mostraQuota(min)});
  return t('Quota da {{min}} a {{max}}', {min: mostraQuota(min), max: mostraQuota(max)});
}

function rigaImporti(dati) {
  const posta = soldi(dati.posta);
  if (dati.vincitaMax != null && dati.vincitaMax === dati.vincitaMin) {
    return t('Posta {{posta}} · vincita potenziale {{vincita}}', {posta, vincita: soldi(dati.vincitaMax)});
  }
  return t('Posta {{posta}} · vincita potenziale da {{min}} a {{max}}', {
    posta,
    min: soldi(dati.vincitaMin),
    max: dati.vincitaMax == null ? '—' : soldi(dati.vincitaMax),
  });
}

/**
 * Il messaggio di una giocata condivisa.
 *
 * @param dati    la giocata come la da' datiStampa (stampaGiocata.js)
 * @param importi true per scrivere posta e vincita
 * @param link    il collegamento «Gioca anche tu», o niente
 * @param mia     la giocata e' di chi condivide
 */
export function testoGiocata({dati, importi = false, link = '', mia = false} = {}) {
  const d = dati || {eventi: []};
  const righe = [intestazione(d, mia)];
  (d.eventi || []).forEach((e) => righe.push(rigaSelezione(e)));
  const quota = rigaQuota(d);
  if (quota) righe.push(quota);
  if (importi) righe.push(rigaImporti(d));
  if (link) righe.push(t('Gioca anche tu: {{link}}', {link}));
  return righe.join('\n');
}

/**
 * Il messaggio di una prenotazione: quello di sempre («Ti ho preparato una
 * giocata»), con l'importo proposto e la vincita scritti nella valuta della
 * skin invece dell'euro fisso, poi il codice e il collegamento.
 *
 * La regola degli importi nascosti qui non vale: l'importo e' la proposta.
 *
 * @param prenotazione la prenotazione (vedi datiPrenotazione)
 * @param link         il collegamento; se manca vale quello della prenotazione
 */
export function testoPrenotazione({prenotazione, link} = {}) {
  const d = datiPrenotazione(prenotazione);
  let prima = d.quante === 1
    ? t('Ti ho preparato una giocata: 1 evento')
    : t('Ti ho preparato una giocata: {{n}} eventi', {n: d.quante});
  if (d.quota != null) prima += ', ' + t('quota {{quota}}', {quota: mostraQuota(d.quota)});
  const righe = [prima + '.'];
  if (d.importo != null && d.vincita != null) {
    righe.push(t('{{importo}} per vincere {{vincita}}.', {importo: soldi(d.importo), vincita: soldi(d.vincita)}));
  } else if (d.importo != null) {
    righe.push(t('Importo proposto {{importo}}.', {importo: soldi(d.importo)}));
  }
  if (d.codice) righe.push(t('Codice {{codice}}', {codice: d.codice}));
  const collegamento = link || d.link;
  if (collegamento) righe.push(collegamento);
  return righe.join('\n');
}

/**
 * Il testo per i canali che lo portano nell'indirizzo (wa.me, mailto, sms,
 * x, viber): un indirizzo troppo lungo non si apre, quindi le selezioni si
 * fermano a `massimo` e si dice quante ne restano. Le altre righe
 * (intestazione, quota, importi, «Gioca anche tu») restano tutte. Copia,
 * Signal e Altro… portano il testo intero.
 */
export function perIndirizzo(testo, {massimo = 20} = {}) {
  const righe = String(testo || '').split('\n');
  const fuori = [];
  let selezioni = 0;
  let tolte = 0;
  let dove = -1;
  righe.forEach((r) => {
    if (r.startsWith(PUNTO)) {
      selezioni += 1;
      if (selezioni > massimo) {
        tolte += 1;
        if (dove < 0) dove = fuori.length;
        return;
      }
    }
    fuori.push(r);
  });
  if (tolte > 0) {
    fuori.splice(dove, 0, tolte === 1
      ? t('… e un altro evento')
      : t('… e altri {{n}} eventi', {n: tolte}));
  }
  return fuori.join('\n');
}

/**
 * L'indirizzo di un canale, o null per quelli che sono azioni (signal,
 * copia, immagine, altro) e per facebook senza collegamento (il suo
 * indirizzo prende un collegamento e nessun testo).
 */
export function indirizzo(canale, {testo = '', link = '', oggetto = ''} = {}) {
  const e = encodeURIComponent;
  switch (canale) {
    case 'whatsapp':
      return `https://wa.me/?text=${e(testo)}`;
    case 'telegram':
      /* t.me vuole un collegamento in `url`; senza, il testo va in `url`
         (funziona nelle applicazioni di oggi, ma non e' scritto da nessuna
         parte: da riprovare quando cambiano). */
      if (link) {
        /* Telegram mette il collegamento sopra il testo: la riga che lo
           conteneva («Gioca anche tu: …») si toglie intera, se no resta
           «Gioca anche tu:» in fondo senza niente dopo. */
        const senza = String(testo).split('\n').filter((r) => !r.includes(link)).join('\n').trim();
        return `https://t.me/share/url?url=${e(link)}&text=${e(senza)}`;
      }
      return `https://t.me/share/url?url=${e(testo)}`;
    case 'email':
      return `mailto:?subject=${e(oggetto)}&body=${e(testo)}`;
    case 'sms':
      return `sms:?&body=${e(testo)}`;
    case 'x':
      return `https://x.com/intent/tweet?text=${e(testo)}`;
    case 'facebook':
      return link ? `https://www.facebook.com/sharer/sharer.php?u=${e(link)}` : null;
    case 'viber':
      return `viber://forward?text=${e(testo)}`;
    default:
      return null;
  }
}

/* Una scheda nuova solo per i siti (https): mailto:, sms: e viber: aprono
   un'applicazione, e con target=_blank il computer lascia dietro una scheda
   vuota. */
export function apreNuovaScheda(href) {
  return /^https:\/\//i.test(String(href || ''));
}

/**
 * I canali da mostrare, nell'ordine giusto: quelli accesi nelle regole, meno
 * quelli che qui non funzionano.
 *   - sms solo col dito (pointer: coarse): un computer non manda SMS;
 *   - facebook solo con un collegamento;
 *   - altro solo dove c'e' il foglio del sistema (navigator.share).
 * Signal resta: dove non c'e' il foglio, o col puntatore fine, si copia.
 */
export function canaliVisibili(canali, {haLink = false, puntatoreGrosso = false, puoCondividere = false} = {}) {
  const accesi = canali || {};
  return CANALI.filter((k) => {
    if (accesi[k] !== true) return false;
    if (k === 'sms' && !puntatoreGrosso) return false;
    if (k === 'facebook' && !haLink) return false;
    if (k === 'altro' && !puoCondividere) return false;
    return true;
  });
}

/** Signal si fa copiando: senza foglio del sistema, o sul computer anche se il foglio c'e'. */
export function signalCopiando({puntatoreGrosso = false, puoCondividere = false} = {}) {
  return !puoCondividere || !puntatoreGrosso;
}

/** Il dito o il mouse: matchMedia('(pointer: coarse)'), falso dove non si sa. */
export function puntatoreGrosso() {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && !!window.matchMedia('(pointer: coarse)').matches;
  } catch (e) {
    return false;
  }
}

/** C'e' il foglio del sistema? */
export function puoCondividere() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/* L'ultima strada per copiare: una casella nascosta, selezionata, e il
   comando copia del documento. Serve dove navigator.clipboard non c'e' (una
   pagina non https) o dice di no. */
function copiaDallaCasella(testo) {
  if (typeof document === 'undefined' || !document.body) return false;
  const prima = document.activeElement;
  const casella = document.createElement('textarea');
  casella.value = testo;
  casella.setAttribute('readonly', '');
  casella.setAttribute('aria-hidden', 'true');
  casella.setAttribute('tabindex', '-1');
  Object.assign(casella.style, {position: 'fixed', left: '-10000px', top: '0', opacity: '0'});
  document.body.appendChild(casella);
  let fatto = false;
  try {
    // Il comando copia prende la selezione di chi ha il fuoco.
    try { casella.focus({preventScroll: true}); } catch (e) { casella.focus(); }
    casella.select();
    casella.setSelectionRange(0, casella.value.length);
    fatto = typeof document.execCommand === 'function' && document.execCommand('copy') === true;
  } catch (e) {
    fatto = false;
  }
  casella.remove();
  if (prima && typeof prima.focus === 'function') {
    try { prima.focus(); } catch (e) { /* era gia' sparito */ }
  }
  return fatto;
}

/**
 * Copia un testo negli appunti. Torna una promessa con true se e' riuscito.
 * Prima navigator.clipboard; se manca o rifiuta, la casella nascosta.
 */
export async function copiaTesto(testo) {
  const s = String(testo == null ? '' : testo);
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch (e) {
    /* rifiutato (permessi, pagina non in primo piano): si prova l'altra strada */
  }
  return copiaDallaCasella(s);
}
